import type {
  CreateDestinationPayload,
  DestinationCenterPoint,
  DestinationFormValue,
  DestinationTranslationValue,
  IAreaBoundary,
  UpdateDestinationPayload,
} from '@/shared/api/types/destination-types'
import { buildLocalizedDataPayload } from '@/features/destination/destination-form-model/translations'

/** Blank optional strings are sent as `null` so the backend clears the value instead of storing "". */
function emptyToNull(value: string): string | null {
  return value.trim() || null
}

type DestinationDetailsPayload = Pick<
  CreateDestinationPayload,
  'countryId' | 'provinceId' | 'slug' | 'destinationType' | 'banner' | 'thumbnail' | 'isActive'
>

/** Body of the "basic info" section save (map and translations are saved separately). */
export function buildDetailsUpdatePayload(form: DestinationFormValue): DestinationDetailsPayload {
  return {
    countryId: form.countryId,
    provinceId: form.provinceId,
    slug: form.slug.trim(),
    destinationType: emptyToNull(form.destinationType),
    banner: emptyToNull(form.banner),
    thumbnail: emptyToNull(form.thumbnail),
    isActive: form.isActive,
  }
}

/** Body of the map section save. The API takes the polygon's rings, not the GeoJSON wrapper. */
export function buildMapUpdatePayload(
  boundary: IAreaBoundary,
  center: DestinationCenterPoint,
): UpdateDestinationPayload {
  return { areaBoundary: boundary.coordinates, centerPoint: center }
}

/** Body of the translations section save. */
export function buildTranslationsUpdatePayload(
  translations: DestinationTranslationValue[],
): UpdateDestinationPayload {
  return { localizedData: buildLocalizedDataPayload(translations) }
}

/** Body of `POST /destinations`; `translations` is `null` when they should not be sent. */
export function buildCreateDestinationPayload(
  form: DestinationFormValue,
  boundary: IAreaBoundary,
  center: DestinationCenterPoint,
  translations: DestinationTranslationValue[] | null,
): CreateDestinationPayload {
  return {
    ...buildDetailsUpdatePayload(form),
    ...buildMapUpdatePayload(boundary, center),
    ...(translations ? { localizedData: buildLocalizedDataPayload(translations) } : {}),
  }
}
